import React from 'react';
import {
  HStack,
  Box,
  Text,
  Icon,
  Badge,
  BadgeText,
  Pressable,
  Spinner,
} from '@gluestack-ui/themed';
import {
  User, TriangleAlert, ChevronRight,
} from 'lucide-react-native';
import type { PendingEvaluation } from '@/common/api/models';
import { formatEnglishCalendarDate } from '@/common/utils/formatEnglishDateTime';

interface EvaluationTableRowProps {
  evaluation: PendingEvaluation;
  onSelect: () => void;
}

/**
 * Table row component for displaying an evaluation (pending or completed).
 * Shows student name, module, dates, status, and grade.
 */
export const EvaluationTableRow: React.FC<EvaluationTableRowProps> = ({
  evaluation,
  onSelect,
}) => {
  const isProcessing = evaluation.state === 'processing';

  // Check if overdue (for visual indicator)
  const overDue = evaluation.state === 'submitted' && evaluation.dueDate
    ? new Date(evaluation.dueDate) < new Date()
    : false;
  const daysOverdue = overDue && evaluation.dueDate
    ? Math.floor((new Date().getTime() - new Date(evaluation.dueDate).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  const avatarBg = evaluation.state === 'graded'
    ? '$statusSuccessSubtle'
    : isProcessing
      ? '$statusInfoSubtle'
      : overDue
        ? '$statusErrorSubtle'
        : '$bgBrandSubtle';

  const avatarIconColor = evaluation.state === 'graded'
    ? '$statusSuccess'
    : isProcessing
      ? '$statusInfo'
      : overDue
        ? '$statusError'
        : '$brandBase';

  // Determine status badge based on evaluation.state
  const getStatusBadge = () => {
    if (evaluation.state === 'graded') {
      return (
        <Badge bg="$statusSuccessSubtle" variant="solid" borderRadius="$sm">
          <BadgeText color="$statusSuccess">Graded</BadgeText>
        </Badge>
      );
    }

    if (evaluation.state === 'processing') {
      return (
        <Badge bg="$statusInfoSubtle" variant="solid" borderRadius="$sm">
          <HStack space="xs" alignItems="center">
            {/* Numeric size (px) — same pattern as GenerateWithAIBadge; size="small" is too large in badges */}
            <Spinner size={10} color="$statusInfo" />
            <BadgeText color="$statusInfo">Processing</BadgeText>
          </HStack>
        </Badge>
      );
    }

    if (evaluation.state === 'submitted') {
      if (overDue) {
        return (
          <Badge bg="$statusErrorSubtle" variant="solid" borderRadius="$sm">
            <BadgeText color="$statusError">
              {daysOverdue}
              d overdue
            </BadgeText>
          </Badge>
        );
      }

      return (
        <Badge bg="$statusWarningSubtle" variant="solid" borderRadius="$sm">
          <BadgeText color="$statusWarning">Submitted</BadgeText>
        </Badge>
      );
    }

    // Fallback for any other state
    return (
      <Badge bg="$bgSubtle" variant="solid" borderRadius="$sm">
        <BadgeText color="$textSubtle">{evaluation.state || 'Unknown'}</BadgeText>
      </Badge>
    );
  };

  return (
    <Pressable
      onPress={isProcessing ? undefined : onSelect}
      disabled={isProcessing}
      px="$6"
      py="$4"
      borderBottomWidth={1}
      borderBottomColor="$borderLight"
      bg="$white"
      testID={`evaluation-row-${evaluation.userId}`}
      opacity={isProcessing ? 0.85 : 1}
      $hover={isProcessing ? undefined : {
        backgroundColor: '$bgHover',
      }}
    >
      <HStack alignItems="center">
        {/* Student Name */}
        <HStack w={300} space="sm" alignItems="center">
          <Box
            p="$1.5"
            borderRadius="$md"
            bg={avatarBg}
          >
            <Icon
              as={User}
              size="xs"
              color={avatarIconColor}
            />
          </Box>
          <Text size="sm" fontWeight="medium" color="$textHeading" pr="$2" numberOfLines={1}>
            {evaluation.student}
          </Text>
        </HStack>

        {/* Module */}
        <Box flex={1}>
          <Text size="sm" color="$textBody" numberOfLines={1}>
            {evaluation.module}
          </Text>
        </Box>

        {/* Due Date */}
        <HStack w={110} space="xs" alignItems="center">
          {overDue && <Icon as={TriangleAlert} size="xs" color="$statusError" />}
          <Text
            size="sm"
            color={overDue ? '$statusError' : '$textBody'}
            fontWeight={overDue ? 'semibold' : 'normal'}
          >
            {evaluation.dueDate ? formatEnglishCalendarDate(evaluation.dueDate) : '-'}
          </Text>
        </HStack>

        {/* Submitted Date */}
        <Box w={110}>
          <Text size="sm" color="$textSubtle">
            {evaluation.submittedAt
              ? formatEnglishCalendarDate(evaluation.submittedAt)
              : '-'}
          </Text>
        </Box>

        {/* Status */}
        <Box w={100} alignItems="flex-start">
          {getStatusBadge()}
        </Box>

        {/* Grade */}
        <Box w={80}>
          {evaluation.state === 'graded' && evaluation.grade ? (
            <Text size="sm" color="$textBody">
              {evaluation.grade}
            </Text>
          ) : (
            <Text size="sm" color="$textHint">
              Not Graded
            </Text>
          )}
        </Box>

        {/* Trailing chevron — omit while processing (badge spinner is the only affordance) */}
        <Box w={40} alignItems="flex-end">
          {!isProcessing ? (
            <Icon as={ChevronRight} size="sm" color="$textHint" />
          ) : null}
        </Box>
      </HStack>
    </Pressable>
  );
};

export default EvaluationTableRow;
